import { useState, useRef } from "react";
import { weeklyEvents, EVENT_CATEGORIES } from "../../../mocks/events";
import type { BursaEvent } from "../../../mocks/events";

const DAYS = ["Pzt", "Sal", "Çrş", "Prş", "Cum", "Cmt", "Paz"];
const DAY_NUMS = ["30", "31", "1", "2", "3", "4", "5"];
const DAY_MONTHS = ["Mart", "Mart", "Nisan", "Nisan", "Nisan", "Nisan", "Nisan"];

function EventCard({ event }: { event: BursaEvent }) {
  const [saved, setSaved] = useState(false);

  return (
    <div className="group bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-stone-200 transition-all flex-shrink-0 w-[300px] sm:w-auto">
      {/* Image */}
      <div className="relative h-44 overflow-hidden">
        <img
          src={event.image}
          alt={event.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

        {/* Date Badge */}
        <div className="absolute top-3 left-3 bg-white rounded-xl px-2.5 py-1.5 text-center min-w-[44px]">
          <div className="text-xs font-semibold text-stone-500 leading-none">{event.month}</div>
          <div className="text-xl font-bold text-anthracite-900 leading-tight">{event.dayNum}</div>
          <div className="text-xs text-stone-400 leading-none">{event.dayShort}</div>
        </div>

        {/* Save Button */}
        <button
          onClick={() => setSaved(!saved)}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/90 hover:bg-white cursor-pointer transition-colors"
        >
          <i className={`${saved ? "ri-bookmark-fill text-forest-600" : "ri-bookmark-line text-stone-500"} text-sm`} />
        </button>

        {/* Category Tag */}
        <div className="absolute bottom-3 left-3">
          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${event.categoryColor}`}>
            {event.category}
          </span>
        </div>

        {event.isFeatured && (
          <div className="absolute bottom-3 right-3">
            <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-semibold">Öne Çıkan</span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-bold text-anthracite-900 text-sm leading-snug mb-2 line-clamp-2">{event.title}</h3>

        <div className="space-y-1.5 mb-3">
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-time-line text-stone-400 text-sm" />
            </div>
            <span>{event.time}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-stone-500">
            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
              <i className="ri-map-pin-line text-stone-400 text-sm" />
            </div>
            <span className="truncate">{event.location}</span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className={`text-sm font-bold ${event.isFree ? "text-forest-600" : "text-anthracite-800"}`}>
            {event.price}
          </div>
          <div className="flex items-center gap-1.5">
            {event.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded-full whitespace-nowrap">{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EventsSection() {
  const [activeDay, setActiveDay] = useState("Tümü");
  const [activeCategory, setActiveCategory] = useState("Tümü");
  const scrollRef = useRef<HTMLDivElement>(null);

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({ left: dir === "right" ? 340 : -340, behavior: "smooth" });
  };

  const filteredEvents = weeklyEvents.filter((e) => {
    const dayMatch = activeDay === "Tümü" || e.dayShort === activeDay;
    const catMatch = activeCategory === "Tümü" || e.category === activeCategory;
    return dayMatch && catMatch;
  });

  const eventCountByDay = (day: string) =>
    weeklyEvents.filter((e) => e.dayShort === day).length;

  return (
    <section className="py-16 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-forest-100 rounded-full mb-3">
              <i className="ri-calendar-event-line text-forest-700 text-sm" />
              <span className="text-xs font-semibold text-forest-700">Bu Hafta</span>
            </div>
            <h2 className="text-3xl font-display font-bold text-anthracite-900 leading-tight">
              Bu Hafta Bursa&apos;da
            </h2>
            <p className="text-stone-500 mt-1.5 text-sm">
              30 Mart – 6 Nisan 2026 arasındaki seçili etkinlikler
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => scroll("left")}
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-500 hover:text-anthracite-800 hover:border-stone-300 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-s-line text-lg" />
            </button>
            <button
              onClick={() => scroll("right")}
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-stone-200 bg-white text-stone-500 hover:text-anthracite-800 hover:border-stone-300 transition-all cursor-pointer"
            >
              <i className="ri-arrow-right-s-line text-lg" />
            </button>
          </div>
        </div>

        {/* Day Selector */}
        <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1">
          <button
            onClick={() => setActiveDay("Tümü")}
            className={`flex flex-col items-center px-4 py-2 rounded-xl border transition-all cursor-pointer flex-shrink-0 ${
              activeDay === "Tümü"
                ? "bg-anthracite-900 border-anthracite-900 text-white"
                : "bg-white border-stone-200 text-stone-600 hover:border-stone-300"
            }`}
          >
            <span className="text-xs font-semibold whitespace-nowrap">Tümü</span>
            <span className="text-xs opacity-70">{weeklyEvents.length} etkinlik</span>
          </button>

          {DAYS.map((day, i) => {
            const count = eventCountByDay(day);
            const isActive = activeDay === day;
            return (
              <button
                key={day}
                onClick={() => setActiveDay(day)}
                className={`flex flex-col items-center px-4 py-2 rounded-xl border transition-all cursor-pointer flex-shrink-0 ${
                  isActive
                    ? "bg-forest-600 border-forest-600 text-white"
                    : "bg-white border-stone-200 text-stone-600 hover:border-stone-300"
                }`}
              >
                <span className="text-xs font-semibold whitespace-nowrap">{day}</span>
                <span className="text-base font-bold leading-tight">{DAY_NUMS[i]}</span>
                <span className="text-xs opacity-70">{DAY_MONTHS[i]}</span>
                {count > 0 && (
                  <span className={`text-xs font-medium mt-0.5 ${isActive ? "text-white/80" : "text-forest-600"}`}>
                    {count} etkinlik
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Category Pills */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1">
          {EVENT_CATEGORIES.slice(0, 6).map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer border flex-shrink-0 ${
                activeCategory === cat
                  ? "bg-anthracite-900 text-white border-anthracite-900"
                  : "bg-white text-stone-600 border-stone-200 hover:border-stone-400"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Events Scroll */}
        {filteredEvents.length > 0 ? (
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-4 scroll-smooth"
            style={{ scrollbarWidth: "none" }}
          >
            <style>{`.events-scroll::-webkit-scrollbar { display: none; }`}</style>
            {filteredEvents.map((event) => (
              <div key={event.id} className="flex-shrink-0 w-[300px] sm:w-[280px] lg:w-[300px]">
                <EventCard event={event} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3">
              <i className="ri-calendar-line text-4xl text-stone-300" />
            </div>
            <p className="text-stone-500 text-sm">Bu gün için etkinlik bulunamadı</p>
            <button
              onClick={() => { setActiveDay("Tümü"); setActiveCategory("Tümü"); }}
              className="mt-3 text-xs text-forest-600 hover:text-forest-700 cursor-pointer"
            >
              Tüm Etkinlikleri Göster
            </button>
          </div>
        )}

        {/* CTA Banner */}
        <div className="mt-8 rounded-2xl overflow-hidden relative">
          <img
            src="https://public.readdy.ai/ai/img_res/709355ca69d0bc2c8c42af9422b5fa36.jpg"
            alt="Bursa Etkinlikleri"
            className="w-full h-36 object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent flex items-center px-6 md:px-10 gap-6">
            <div>
              <h3 className="text-white font-bold text-lg md:text-xl leading-tight mb-1">
                Etkinliğinizi Ekleyin
              </h3>
              <p className="text-white/70 text-sm">
                Bursa'da bir etkinlik mi düzenliyorsunuz? Binlerce ziyaretçiye duyurun.
              </p>
            </div>
            <a
              href="/business"
              className="flex-shrink-0 px-5 py-2.5 bg-white text-anthracite-900 rounded-xl text-sm font-semibold hover:bg-stone-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              Etkinlik Ekle
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
